import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

// ── Alumni data embedded directly from the CSV files ────────────────────────

const FRESHER_ALUMNI = [
  {
    name: 'Aman Aggarwal',
    cohort: "Cohort'23",
    domain: 'Consulting',
    testimonial: `"The comprehensive in-class and out-class curriculum at Masters' Union refined my skills and played a key role in securing my PPO. The Career Advisory Team further supported me throughout — connecting me with mentors and guiding me at every step."`,
    linkedin: 'https://www.linkedin.com/in/aman-agarwal-aa8333192/',
    photo: 'https://i.postimg.cc/W1P31YqF/6.png',
  },
  {
    name: 'Shubham Gupta',
    cohort: "Cohort'21",
    domain: 'General Management',
    testimonial: `"I'm a Principal Consultant at Razorpay, working with enterprise clients on complex payment challenges. From solution consulting to leading top accounts, the journey has been hands-on and fast-paced. Masters' Union shaped my foundation through startup exposure and real-world learning."`,
    linkedin: 'https://www.linkedin.com/in/shubham-gupta1808/',
    photo: 'https://i.postimg.cc/YCw9CDLm/5.png',
  },
  {
    name: 'Yashana Lalwani',
    cohort: "Cohort'25",
    domain: 'General Management',
    testimonial: `"Winning Flipkart Wired 8.0 as a fresher was a turning point, earning me a PPI and role at Flipkart. Masters' Union sharpened my case-solving through its diverse cohort and peer learning."`,
    linkedin: 'https://www.linkedin.com/in/yashanalalwani/',
    photo: 'https://i.postimg.cc/NjwMjC2H/7.png',
  },
  {
    name: 'Aarushi Gosain',
    cohort: "Cohort'25",
    domain: 'Consulting',
    testimonial: `"I'm grateful to the Placement Team for their constant support, connecting me to the right opportunities and guiding my journey. Special thanks to Jyoti and Jasleen for helping me break into consulting and secure my role at KPMG."`,
    linkedin: 'https://www.linkedin.com/in/aarushi-gosain/',
    photo: 'https://i.postimg.cc/7YzhDrhr/8.png',
  },
];

const EXPERIENCED_ALUMNI = [
  {
    name: 'Prashasti Singh',
    cohort: "Cohort'21",
    domain: 'Consulting',
    testimonial: `"I'm an Implementation Coach at McKinsey & Company, working across marketing and sales for industries like energy, building materials, and FMCG. From analyst to coach, the journey has been fast-paced and hands-on, shaped deeply by Masters' Union and its real-world learning."`,
    linkedin: 'https://www.linkedin.com/in/prashastisingh/',
    photo: 'https://i.postimg.cc/cJxLQKcm/1.png',
  },
  {
    name: 'Anirbit Datta',
    cohort: "Cohort'21",
    domain: 'Product Management',
    testimonial: `"I'm a Technical Product Manager at Microsoft, building AI-driven products at scale. From Business Program Manager to shaping product roadmaps, the journey has been fast and hands-on. Masters' Union instilled the rigor, data-driven thinking, and courage to take on big bets."`,
    linkedin: 'https://www.linkedin.com/in/anirbit-datta/',
    photo: 'https://i.postimg.cc/vZLmg753/2.png',
  },
  {
    name: 'Stuti Pandey',
    cohort: "Cohort'23",
    domain: 'Marketing',
    testimonial: `"I'm a Brand Manager at ITC Sunfeast, working on packaging and brand strategy for one of India's top FMCG brands. The MBA helped me transition from digital marketing into brand management — and Masters' Union gave me the business rigour and network to make it happen."`,
    linkedin: 'https://www.linkedin.com/in/stuti-pandey-21a32a140/',
    photo: 'https://i.postimg.cc/6pLQ2VCL/3.png',
  },
  {
    name: 'Gautam Marwah',
    cohort: "Cohort'24",
    domain: 'Finance (VC)',
    testimonial: `"I'm an Analyst at Stellaris Venture Partners, one of India's top seed-stage VC funds. Masters' Union gave me the strategic framework and startup exposure that made the pivot from operations to VC possible — within a year of graduating."`,
    linkedin: 'https://www.linkedin.com/in/gautam-marwah/',
    photo: 'https://i.postimg.cc/9QtfRPyF/4.png',
  },
];

const DOMAIN_COLORS = {
  'Consulting':         { bg: 'rgba(63,168,106,0.10)',  border: 'rgba(63,168,106,0.25)',  text: '#2A7050' },
  'Product Management': { bg: 'rgba(63,115,168,0.10)',  border: 'rgba(63,115,168,0.25)',  text: '#1A4F9A' },
  'Marketing':          { bg: 'rgba(232,160,32,0.10)',  border: 'rgba(232,160,32,0.25)',  text: '#9A6200' },
  'General Management': { bg: 'rgba(149,100,220,0.10)', border: 'rgba(149,100,220,0.25)', text: '#6A30BC' },
  'Finance (VC)':       { bg: 'rgba(217,64,64,0.08)',   border: 'rgba(217,64,64,0.22)',   text: '#B02020' },
};

const getDomainStyle = (domain) =>
  DOMAIN_COLORS[domain] || { bg: 'rgba(0,0,0,0.05)', border: 'rgba(0,0,0,0.12)', text: 'rgba(0,0,0,0.55)' };

const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: [0.2, 0.8, 0.2, 1] }}
    viewport={{ once: true, margin: '-40px' }}
  >
    {children}
  </motion.div>
);

const AlumniCard = ({ alumni, index }) => {
  const ds = getDomainStyle(alumni.domain);
  return (
    <FadeIn delay={index * 0.07}>
      <div style={{
        backgroundColor: '#ffffff',
        border: '1.5px solid rgba(0,0,0,0.08)',
        borderRadius: '14px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.09)'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.14)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; }}
      >
        {/* Large opening quote */}
        <div style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: '52px', lineHeight: 1,
          color: '#E8A020', opacity: 0.40,
          marginBottom: '-6px', marginLeft: '-2px',
          userSelect: 'none',
        }}>
          "
        </div>

        {/* Testimonial */}
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '14px', color: 'rgba(0,0,0,0.68)',
          lineHeight: 1.68, flex: 1, margin: '0 0 18px',
        }}>
          {alumni.testimonial.replace(/^"|"$/g, '')}
        </p>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '14px', flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: '#f5f5f5', border: '2px solid rgba(0,0,0,0.08)' }}>
            <img
              src={alumni.photo}
              alt={alumni.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
              onError={e => {
                e.target.style.display = 'none';
                e.target.parentNode.style.background = '#e0e0e0';
                e.target.parentNode.innerHTML = `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;font-size:15px;font-weight:700;color:rgba(0,0,0,0.45);font-family:DM Sans">${alumni.name.charAt(0)}</span>`;
              }}
            />
          </div>

          {/* Name + cohort */}
          <div style={{ flex: 1, minWidth: '80px' }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#111111', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {alumni.name}
            </p>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.40)', margin: 0 }}>
              {alumni.cohort}
            </p>
          </div>

          {/* Domain badge + LinkedIn in a row that wraps gracefully */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0 }}>
            <span style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '10px', fontWeight: 600,
              color: ds.text, backgroundColor: ds.bg,
              border: `1px solid ${ds.border}`,
              borderRadius: '9999px', padding: '3px 8px',
              whiteSpace: 'nowrap', maxWidth: '120px',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {alumni.domain}
            </span>
            <a href={alumni.linkedin} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', color: 'rgba(0,0,0,0.28)', transition: 'color 0.2s', flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = '#0A66C2'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(0,0,0,0.28)'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </FadeIn>
  );
};

export const AlumniTestimonials = ({ yearsExperience }) => {
  // < 1 year of full-time experience → fresher pool; ≥ 1 year → experienced pool
  const isFresher = !yearsExperience || yearsExperience < 1;
  const alumni    = isFresher ? FRESHER_ALUMNI : EXPERIENCED_ALUMNI;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        backgroundColor: '#fafafa',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '16px',
        padding: 'clamp(24px,5vw,36px)',
        marginBottom: '24px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      {/* Section header */}
      <FadeIn>
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#E8A020' }}>
              Alumni Stories
            </span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,0.08)' }} />
          </div>

          <h2 style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 'clamp(20px,3.5vw,30px)', fontWeight: 800,
            color: '#111111', letterSpacing: '-0.02em', lineHeight: 1.15,
            marginBottom: '10px',
          }}>
            This isn't just theory.{' '}
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
              People have actually done it.
            </span>
          </h2>

          <p style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 'clamp(13px,1.5vw,15px)',
            color: 'rgba(0,0,0,0.50)',
            lineHeight: 1.65,
            maxWidth: '640px',
            margin: 0,
          }}>
            Masters' Union alumni who made career moves and used the PGP programme to pivot,
            accelerate, or completely change their career path.
          </p>
        </div>
      </FadeIn>

      {/* 4 alumni cards — 2×2 grid on desktop, 1 per row on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '14px',
      }} className="alumni-grid">
        {alumni.map((a, i) => (
          <AlumniCard key={a.name} alumni={a} index={i} />
        ))}
      </div>
    </motion.div>
  );
};
